/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import { History } from "history";

import {
    stopLoading,
    setInitialized,
    startProgress,
    progressNext,
    clearProgress,
    setError,
    AppError,
    loadScreen,
    RouteRegistry,
    Router,
    QueryParamsAction,
    getQueryParams,
} from "@ef/feature-app";

import { AppStoreType, RootState, initStore, resetAction } from "@ef/feature-core";

export type AppInitFunction = (
    store: AppStoreType 
) => Promise<void>;


export interface AppConfig {
    initializers: Array<AppInitFunction>
    logStore?: boolean | "DEBUG" | "INFO"
    preload?: RootState
}


export class App {
    private readonly config: AppConfig;
    private store: AppStoreType;

    constructor(config: AppConfig) {
        this.config = config;
        this.store = this._initStore();
    }

    private _initStore() {
        const loglevel = this.config.logStore && (
            this.config.logStore === "DEBUG" ? "debug" : "info"
        );
        return initStore({
            loglevel,
            preloadedState: this.config.preload
        });
    }

    public useHistory(history?: History) {
        if (!history) {
            return;
        }
        Router.useHistory(history);

        // Initialize History updates
        Router.listen(({ location, action }) => {
            // console.log("Route", location, action);
            if (action == "POP" || action == "PUSH") {
                // Dispatch QueryParams Change
                const params = getQueryParams(location.search);
                this.getStore().dispatch(QueryParamsAction(params));

                // Start New routing
                const loader = RouteRegistry.match(location);
                if (loader) {
                    this.getStore().dispatch(loadScreen(loader()));
                }
            }
        });

        // Push Initial screen initialization to initializers
        const screenInit: AppInitFunction = async (initstore) => {
            if (history) {
                // Dispatch Query-Params-Change 
                const params = getQueryParams(Router.location().search);
                initstore.dispatch(QueryParamsAction(params));

                const firstloader = RouteRegistry.match(Router.location());
                //console.log("FIRST LOAD Dispatch", firstloader);
                if (firstloader) {
                    initstore.dispatch(loadScreen(firstloader()));
                    return;
                } else {
                    // Fallback - no init Done and no Router or no Match
                    // This should not happen.
                    // eslint-disable-next-line no-console
                    console.log("Fallback-Routing to USER_DEFAULT");
                    initstore.dispatch(loadScreen({
                        screen: { name: "USER_DEFAULT" }
                    }));
                }
            }
        };
        // Append Screen init at the end
        this.config.initializers.push(screenInit);
    }

    getStore() {
        if (!this.store) {
            this.store = this._initStore();
        }
        return this.store;
    }

    reset(): void {
        this.store.dispatch(resetAction());
    }

    async init() {
        const count = this.config.initializers?.length;
        if (count > 0) {
            this.store.dispatch(startProgress(count));
            for (const init of this.config.initializers) {
                try {
                    this.store.dispatch(progressNext());
                    await init(this.store);
                } catch(err) {
                    if (err instanceof Error) {
                        this.store.dispatch(setError({
                            title: "Unknown Error at initialization",
                            message: err.message
                        }));
                    } else {
                        this.store.dispatch(setError({
                            title: "Unknown Error at initialization"
                        } as AppError));
                    }
                    this.store.dispatch(clearProgress());
                    return;
                }
            }
            this.store.dispatch(clearProgress());
        }
        this.store.dispatch(setInitialized(true));
        this.store.dispatch(stopLoading());
    }
}
